import { accessControl } from 'lib-shared-access-control';
import { User } from 'lib-shared-types-api';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { hasBooleanFeatures } from 'components/shared/featureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import { useFeatureFlag } from '../../../../../../utils/featureFlagging/useFeatureFlag';
import { useMediaQuery } from '../../../../../../utils/mediaQuery';
import { getSettingsMenuCategories } from '../menuCategories';
import { type MenuCategory } from '../types';

export const useJabraRoutes = (): boolean => {
  const [isOpenedInPWA] = useMediaQuery('(display-mode: standalone)');
  const flags = useFeatureFlag(['web-usb-jabra-integration']);

  return flags.webUsbJabraIntegration && isOpenedInPWA;
};

/**
 * Checks if the user has access to the menu item based on the useCopilotTrial
 * hook results. If the menu item is not part of copilot it will return true.
 *
 * @param id - The id of the menu item.
 * @param hasCopilot - The copilot status of the user.
 * @param eligibleForTrial - The copilot trial status of the user.
 */
const hasCopilotTrialAcccess = (
  id: string,
  hasCopilot: boolean,
  eligibleForTrial: boolean,
) => {
  switch (id) {
    case 'copilot-trial':
      return eligibleForTrial;
    case 'ai-style-guide':
    case 'ai-options':
      return hasCopilot;
    default:
      return true;
  }
};

/** Creates a setting menu depending on the role of current agent. */
export function useSettingsMenu(): readonly MenuCategory[] {
  const { user, allFeatureFlags } = useAppSelector(
    (state) => ({
      allFeatureFlags: state.settings.profile.featureflags,
      user: state.settings.profile.profile as User,
    }),
    shallowEqual,
  );
  const showJabraRoutes = useJabraRoutes();
  const { hasCopilot, eligibleForTrial } = useCopilotTrial();

  return useMemo(
    () =>
      getSettingsMenuCategories().reduce((menu, category) => {
        const accessibleMenuItems = category.items.filter((item) => {
          // personalSettingsRevamp is temp flag
          if (
            item.title === 'Integrations' &&
            !showJabraRoutes &&
            user.roles.includes('agent') &&
            !user.roles.includes('admin') &&
            allFeatureFlags['personal-settings-revamp']
          ) {
            return false;
          }

          const hasAccess = item.accessControl
            ? accessControl.hasAccess(
                ...(item.accessControl as Parameters<
                  typeof accessControl.hasAccess
                >),
              )
            : true;

          return (
            hasAccess &&
            hasBooleanFeatures(
              allFeatureFlags,
              item.featureFlags,
              item.featureFlagsStrategy,
            ) &&
            hasCopilotTrialAcccess(item.id, hasCopilot, eligibleForTrial)
          );
        });

        if (accessibleMenuItems.length) {
          return [...menu, { ...category, items: accessibleMenuItems }];
        }

        return menu;
      }, [] as readonly MenuCategory[]),
    [showJabraRoutes, user, allFeatureFlags, hasCopilot, eligibleForTrial],
  );
}
